import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getStatsByChannel, StatsByChannelPayload } from '../../../services'
import { ContactChanel } from '../../../types/utils'
import { Typogaphy } from 'vnetwork-ui'
import { Radio } from 'lucide-react'
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts'
import { HalfChartSkeleton } from './skeletons'

interface StatsByChannelChartProps {
    filterPayload: StatsByChannelPayload
}

const StatsByChannelChart: React.FC<StatsByChannelChartProps> = ({
    filterPayload,
}) => {
    const { t } = useTranslation('marketing')

    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.MARKETING, 'stats-by-channel', filterPayload],
            queryFn: () => getStatsByChannel(filterPayload),
        },
        { loadingComponent: <HalfChartSkeleton /> }
    )

    const stats = data?.results || []

    const chartData = useMemo(() => {
        return ContactChanel(t)?.map((channel) => {
            const found = stats.find((item) => item?.channel === channel.value)
            return {
                name: channel.label.toUpperCase(),
                unclassified: found?.unclassified ?? 0,
                warm: found?.warm_lead ?? 0,
                sql: found?.sale_qualified_lead ?? 0,
                cool: found?.cool_lead ?? 0,
                unqualified: found?.un_qualified_lead ?? 0,
            }
        })
    }, [stats])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="bg-white rounded-md border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-4">
                <Radio size={16} className="text-primary" />
                <Typogaphy className="font-medium text-primary capitalize">
                    {t('stats-by-channel')}
                </Typogaphy>
            </div>

            <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                        dataKey="name"
                        tick={{ fontSize: 9 }}
                        interval={0}
                        angle={-25}
                        textAnchor="end"
                        height={70}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip
                        contentStyle={{
                            fontSize: 12,
                            borderRadius: 8,
                            border: '1px solid #e5e7eb',
                        }}
                    />
                    <Legend
                        wrapperStyle={{ fontSize: 11 }}
                        iconType="square"
                        iconSize={10}
                    />
                    <Bar
                        dataKey="unclassified"
                        name={t('unclassified')}
                        stackId="a"
                        fill="#9ca3af"
                    />
                    <Bar
                        dataKey="warm"
                        name={t('warm-lead')}
                        stackId="a"
                        fill="#f59e0b"
                    />
                    <Bar dataKey="sql" name="SQL" stackId="a" fill="#22c55e" />
                    <Bar
                        dataKey="cool"
                        name={t('cool-lead')}
                        stackId="a"
                        fill="#3b82f6"
                    />
                    <Bar
                        dataKey="unqualified"
                        name={t('unqualified')}
                        stackId="a"
                        fill="#ef4444"
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    )
}

export default StatsByChannelChart
